/**
 * An in-memory family for building and reviewing the page before apps/api
 * lands: the same `ProfileApi` surface, every write applied to the data
 * below after a short delay so the optimistic UI is visible. No external
 * image URLs; avatars render as initials.
 */
import type { ProfileApi } from './api';
import { ApiError } from './api';
import type {
  Address,
  CampaignCommitment,
  OutsideRelative,
  Commitment,
  EmergencyContact,
  FinderGroup,
  Giving,
  Invoice,
  Lookups,
  MedicalItem,
  MedicalProfile,
  MedicalType,
  MyGroup,
  Person,
  ProfileMe,
  RelationshipRequest,
  Subscriptions,
  FormerMember,
} from './types';

const wait = (ms = 350) => new Promise<void>((r) => setTimeout(r, ms));

/** MP hands back wall clock with no zone; the mock does the same. */
function nowWall(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

const CAMPUSES = [
  { id: 2, name: 'Troy Campus', slug: 'troy' },
  { id: 3, name: 'Lake Orion Campus', slug: 'lake-orion' },
  { id: 5, name: 'Farmington Hills Campus', slug: 'farmington-hills' },
  { id: 7, name: 'Romeo Campus', slug: 'romeo' },
  { id: 9, name: 'Warren Campus', slug: 'warren' },
  { id: 11, name: 'Plymouth Campus', slug: 'plymouth' },
];

/** The shape /locations returns (latitude/longitude ride along with the LocationGroup fields). */
const MOCK_LOCATIONS = [
  { id: 2, name: 'Troy', lat: 42.5622, lng: -83.1513, addr: '6600 Rochester Rd', zip: '48085' },
  {
    id: 3,
    name: 'Lake Orion',
    lat: 42.7845,
    lng: -83.2397,
    addr: '1500 W Silverbell Rd',
    zip: '48359',
  },
  {
    id: 5,
    name: 'Farmington Hills',
    lat: 42.4837,
    lng: -83.3766,
    addr: '24000 Middlebelt Rd',
    zip: '48336',
  },
  { id: 7, name: 'Romeo', lat: 42.8137, lng: -83.0122, addr: '20250 32 Mile Rd', zip: '48065' },
  { id: 9, name: 'Warren', lat: 42.5145, lng: -83.0147, addr: '31000 Mound Rd', zip: '48092' },
  {
    id: 11,
    name: 'Plymouth',
    lat: 42.3714,
    lng: -83.4702,
    addr: '45640 Ann Arbor Rd',
    zip: '48170',
  },
].map((c) => ({
  id: c.id,
  primary_congregation_id: c.id,
  svg_url: null,
  city: c.name,
  address: c.addr,
  state: 'MI',
  zip: c.zip,
  latitude: c.lat,
  longitude: c.lng,
  congregations: [
    {
      id: c.id,
      name: `${c.name} Campus`,
      display_name: c.name,
      campus_slug: c.name.toLowerCase().replace(/\s+/g, '-'),
      service_times: 'Sundays 9 & 11 AM',
    },
  ],
}));

const lookups: Lookups = {
  prefixes: [
    { id: 1, label: 'Mr.' },
    { id: 2, label: 'Mrs.' },
    { id: 3, label: 'Ms.' },
    { id: 4, label: 'Dr.' },
  ],
  suffixes: [
    { id: 1, label: 'Jr.' },
    { id: 2, label: 'Sr.' },
    { id: 3, label: 'III' },
  ],
  genders: [
    { id: 1, label: 'Male' },
    { id: 2, label: 'Female' },
  ],
  maritalStatuses: [
    { id: 1, label: 'Single' },
    { id: 2, label: 'Married' },
    { id: 3, label: 'Divorced' },
    { id: 4, label: 'Widowed' },
    { id: 5, label: 'Partnered' },
  ],
  householdPositions: [
    { id: 1, label: 'Head of Household' },
    { id: 2, label: 'Minor Child' },
    { id: 3, label: 'Other Adult' },
    { id: 4, label: 'Adult Child' },
  ],
  shirtSizes: [
    { id: 1, label: '2T', group: 'Toddler', sort: 1 },
    { id: 2, label: '3T', group: 'Toddler', sort: 2 },
    { id: 3, label: '4T', group: 'Toddler', sort: 3 },
    { id: 4, label: 'YXS', group: 'Youth', sort: 4 },
    { id: 5, label: 'YS', group: 'Youth', sort: 5 },
    { id: 6, label: 'YM', group: 'Youth', sort: 6 },
    { id: 7, label: 'YL', group: 'Youth', sort: 7 },
    { id: 8, label: 'YXL', group: 'Youth', sort: 8 },
    { id: 9, label: 'XS', group: 'Adult', sort: 9 },
    { id: 10, label: 'S', group: 'Adult', sort: 10 },
    { id: 11, label: 'M', group: 'Adult', sort: 11 },
    { id: 12, label: 'L', group: 'Adult', sort: 12 },
    { id: 13, label: 'XL', group: 'Adult', sort: 13 },
    { id: 14, label: '2XL', group: 'Adult', sort: 14 },
    { id: 15, label: '3XL', group: 'Adult', sort: 15 },
  ],
  relationshipTypes: [
    {
      id: 1,
      name: 'Family',
      sortOrder: 1,
      relationships: [
        { id: 1, name: 'Married to', maleLabel: 'Husband', femaleLabel: 'Wife', reciprocalId: 1 },
        { id: 2, name: 'Child', maleLabel: 'Son', femaleLabel: 'Daughter', reciprocalId: 3 },
        { id: 3, name: 'Parent', maleLabel: 'Father', femaleLabel: 'Mother', reciprocalId: 2 },
        { id: 4, name: 'Sibling', maleLabel: 'Brother', femaleLabel: 'Sister', reciprocalId: 4 },
        {
          id: 5,
          name: 'Grandchild',
          maleLabel: 'Grandson',
          femaleLabel: 'Granddaughter',
          reciprocalId: 6,
        },
        {
          id: 6,
          name: 'Grandparent',
          maleLabel: 'Grandfather',
          femaleLabel: 'Grandmother',
          reciprocalId: 5,
        },
        {
          id: 7,
          name: 'Step-child',
          maleLabel: 'Stepson',
          femaleLabel: 'Stepdaughter',
          reciprocalId: 8,
        },
        {
          id: 8,
          name: 'Step-parent',
          maleLabel: 'Stepfather',
          femaleLabel: 'Stepmother',
          reciprocalId: 7,
        },
      ],
    },
    {
      id: 2,
      name: 'Extended family',
      sortOrder: 2,
      relationships: [
        { id: 9, name: 'Aunt / Uncle', maleLabel: 'Uncle', femaleLabel: 'Aunt', reciprocalId: 10 },
        {
          id: 10,
          name: 'Niece / Nephew',
          maleLabel: 'Nephew',
          femaleLabel: 'Niece',
          reciprocalId: 9,
        },
        { id: 11, name: 'Cousin', maleLabel: null, femaleLabel: null, reciprocalId: 11 },
        { id: 12, name: 'In-law', maleLabel: null, femaleLabel: null, reciprocalId: 12 },
      ],
    },
    {
      id: 3,
      name: 'Care',
      sortOrder: 3,
      relationships: [
        { id: 13, name: 'Guardian', maleLabel: null, femaleLabel: null, reciprocalId: 14 },
        { id: 14, name: 'Ward', maleLabel: null, femaleLabel: null, reciprocalId: 13 },
        { id: 15, name: 'Caregiver', maleLabel: null, femaleLabel: null, reciprocalId: null },
      ],
    },
  ],
  congregations: CAMPUSES,
};

function person(
  p: Partial<Person> & { contactId: number; firstName: string; lastName: string }
): Person {
  return {
    nickname: null,
    imageUrl: null,
    email: null,
    mobilePhone: null,
    dateOfBirth: null,
    age: null,
    genderId: null,
    householdPositionId: 1,
    position: 'Head of Household',
    isChild: false,
    shirtSizeId: null,
    relationship: null,
    emergencyContact: null,
    relations: [],
    family: [],
    privacy: {
      emailUnlisted: false,
      mobileUnlisted: false,
      removeFromDirectory: false,
      doNotText: false,
      bulkEmailOptOut: false,
    },
    displayName: `${p.nickname ?? p.firstName} ${p.lastName}`,
    ...p,
  };
}

const ME_ID = 100;

/** The See Jesus card the family filled in on the campaign page (campaign 194). */
const campaignCommitments: CampaignCommitment[] = [
  {
    commitmentId: 244,
    campaignId: 194,
    campaignName: 'Future 2026 Campaign',
    campaignStart: '2025-06-01',
    campaignEnd: null,
    giveUrl: null,
    contactId: ME_ID,
    includesSpouse: true,
    commitmentDate: '2026-09-11',
    previousAnnual: 8000,
    expandedAnnual: 500,
    storedResources: 0,
    projectedAnnual: 8500,
    projectedTwoYear: 17000,
    total: 17000,
    statusId: 4,
    pledgeId: 9001,
  },
];
const spouse = person({
  contactId: 101,
  firstName: 'Rachel',
  lastName: 'Bennett',
  email: 'rachel.bennett@example.com',
  mobilePhone: '(248) 555-0188',
  dateOfBirth: '1988-07-19',
  age: 38,
  genderId: 2,
  householdPositionId: 1,
  position: 'Head of Household',
  shirtSizeId: 11,
  relationship: {
    contactRelationshipId: 9001,
    relationshipId: 1,
    label: 'Wife',
    typeId: 1,
    typeName: 'Family',
  },
  emergencyContact: {
    kind: 'person',
    contactId: ME_ID,
    name: 'Daniel Bennett',
    phone: '(248) 555-0142',
    imageGuid: null,
  },
});
const kid1 = person({
  contactId: 102,
  firstName: 'Nora',
  lastName: 'Bennett',
  dateOfBirth: '2014-03-02',
  age: 12,
  genderId: 2,
  householdPositionId: 2,
  position: 'Minor Child',
  isChild: true,
  shirtSizeId: 7,
  relationship: {
    contactRelationshipId: 9002,
    relationshipId: 2,
    label: 'Daughter',
    typeId: 1,
    typeName: 'Family',
  },
  emergencyContact: {
    kind: 'person',
    contactId: 101,
    name: 'Rachel Bennett',
    phone: '(248) 555-0188',
    imageGuid: null,
  },
});
const kid2 = person({
  contactId: 103,
  firstName: 'Eli',
  lastName: 'Bennett',
  dateOfBirth: '2018-11-27',
  age: 7,
  genderId: 1,
  householdPositionId: 2,
  position: 'Minor Child',
  isChild: true,
  shirtSizeId: null,
  relationship: null,
  emergencyContact: null,
});

// A friend's kid on the household for check-in: the one person the family
// can archive themselves.
const guest = person({
  contactId: 104,
  firstName: 'Ava',
  lastName: 'Carter',
  dateOfBirth: '2017-06-03',
  age: 9,
  genderId: 2,
  householdPositionId: 5,
  position: 'Guest Child',
  isChild: true,
  shirtSizeId: null,
  relationship: null,
  emergencyContact: null,
});

let me: ProfileMe['me'] = {
  ...person({
    contactId: ME_ID,
    firstName: 'Daniel',
    nickname: 'Dan',
    lastName: 'Bennett',
    email: 'dan.bennett@example.com',
    mobilePhone: '(248) 555-0142',
    dateOfBirth: '1986-02-14',
    age: 40,
    genderId: 1,
    householdPositionId: 1,
    position: 'Head of Household',
    shirtSizeId: 12,
    emergencyContact: {
      kind: 'person',
      contactId: 101,
      name: 'Rachel Bennett',
      phone: '(248) 555-0188',
      imageGuid: null,
    },
  }),
  userId: 5150,
  prefixId: 1,
  middleName: 'James',
  suffixId: null,
  maritalStatusId: 2,
  workPhone: null,
  privacy: {
    emailUnlisted: false,
    mobileUnlisted: true,
    removeFromDirectory: false,
    doNotText: false,
    bulkEmailOptOut: false,
  },
  webCongregationId: 2,
};

let household: NonNullable<ProfileMe['household']> = {
  householdId: 7700,
  name: 'Bennett Household',
  homePhone: null,
  address: {
    line1: '1420 Maple Grove Ln',
    line2: null,
    city: 'Troy',
    state: 'MI',
    zip: '48085',
    lat: 42.6053,
    lng: -83.1216,
  },
  altAddress: {
    line1: '88 Gulfview Dr',
    line2: 'Unit 4B',
    city: 'Naples',
    state: 'FL',
    zip: '34102',
    lat: 26.1364,
    lng: -81.7965,
  },
  congregationId: 2,
  congregationName: 'Troy Campus',
};

const members: Person[] = [spouse, kid1, kid2, guest];
// Archived former members (mock): who left, and their Person to bring back.
const archived = new Map<number, Person>();
let former: FormerMember[] = [
  {
    contactId: 9101,
    firstName: 'Max',
    name: 'Max Turner',
    imageUrl: null,
    position: 'Adult Child',
    leftOn: '2026-05-02T09:00:00',
    canBringBack: false,
    nowHouseholdName: 'Turner',
  },
];

/** "contactId is `relationshipId` of the person", gender-aware for contactId. */
function relation(contactId: number, relationshipId: number, contactRelationshipId: number) {
  const p = [me, ...members].find((x) => x.contactId === contactId)!;
  const t = typeFor(relationshipId);
  return {
    contactId,
    relationship: {
      contactRelationshipId,
      relationshipId,
      label: labelFor(relationshipId, p.genderId),
      typeId: t?.id ?? null,
      typeName: t?.name ?? null,
    },
  };
}
// Dan: Rachel is his wife, Nora his daughter; Eli was never written down (the nudge).
me.relations = [relation(101, 1, 9001), relation(102, 2, 9002)];
spouse.relations = [relation(ME_ID, 1, 9011), relation(102, 2, 9012), relation(103, 2, 9013)];
kid1.relations = [relation(ME_ID, 3, 9021), relation(101, 3, 9022)];
kid2.relations = [relation(101, 3, 9031)];

const family: OutsideRelative[] = (me.family = [
  {
    contactRelationshipId: 9102,
    relationshipId: 23,
    label: 'Central Supervisor',
    typeId: 5,
    typeName: 'Business & Professional',
    editable: false,
    contactId: 211,
    displayName: 'Marcus Hale',
    imageUrl: null,
    householdId: 8801,
  },
  {
    contactRelationshipId: 9101,
    relationshipId: 3,
    label: 'Mother',
    typeId: 1,
    typeName: 'Family',
    editable: true,
    contactId: 210,
    displayName: 'Linda Bennett',
    imageUrl: null,
    householdId: 8800,
  },
]);

let nextId = 5000;
const requests: RelationshipRequest[] = [
  {
    id: 4001,
    fromContactId: 220,
    toContactId: ME_ID,
    from: { contactId: 220, displayName: 'Mark Alvarez', imageUrl: null },
    to: { contactId: ME_ID, displayName: 'Dan Bennett', imageUrl: null },
    relationshipId: 12,
    label: 'In-law',
    note: "Rachel's brother. Adding the family so I can register the kids for camp.",
    status: 'pending',
    createdAt: '2026-09-08T14:22:00',
    respondedAt: null,
  },
  {
    // To one of the kids: any head of household can answer it for her.
    id: 4002,
    fromContactId: 230,
    toContactId: 102,
    from: { contactId: 230, displayName: 'Carol Alvarez', imageUrl: null },
    to: { contactId: 102, displayName: 'Nora Bennett', imageUrl: null },
    relationshipId: 5,
    label: 'Granddaughter',
    note: 'Nana here! Adding Nora so I can pick her up from Wednesday nights.',
    status: 'pending',
    createdAt: '2026-09-21T09:05:00',
    respondedAt: null,
  },
];

const medicalTypes: MedicalType[] = [
  { id: 1, kind: 'allergy', label: 'Peanut', sort: 1 },
  { id: 2, kind: 'allergy', label: 'Tree nut', sort: 2 },
  { id: 3, kind: 'allergy', label: 'Bee sting', sort: 3 },
  { id: 4, kind: 'allergy', label: 'Shellfish', sort: 4 },
  { id: 5, kind: 'allergy', label: 'Egg', sort: 5 },
  { id: 6, kind: 'allergy', label: 'Dairy', sort: 6 },
  { id: 7, kind: 'allergy', label: 'Penicillin', sort: 7 },
  { id: 8, kind: 'allergy', label: 'Seasonal', sort: 8 },
  { id: 10, kind: 'condition', label: 'Asthma', sort: 1 },
  { id: 11, kind: 'condition', label: 'Type 1 diabetes', sort: 2 },
  { id: 12, kind: 'condition', label: 'Seizure disorder', sort: 3 },
  { id: 13, kind: 'condition', label: 'ADHD', sort: 4 },
  { id: 14, kind: 'condition', label: 'Anxiety', sort: 5 },
  { id: 15, kind: 'condition', label: 'Migraines', sort: 6 },
  { id: 20, kind: 'device', label: 'Insulin pump', sort: 1 },
  { id: 21, kind: 'device', label: 'Hearing aid', sort: 2 },
  { id: 22, kind: 'device', label: 'Glasses', sort: 3 },
];

const medical = new Map<number, { items: MedicalItem[]; profile: MedicalProfile | null }>();
medical.set(102, {
  items: [
    {
      id: 1,
      kind: 'allergy',
      typeId: 1,
      label: 'Peanut',
      severe: true,
      summary: null,
      detail: null,
      expiresOn: null,
      keptWhere: null,
      since: '2024-05-12',
    },
    {
      id: 2,
      kind: 'epipen',
      typeId: null,
      label: 'EpiPen',
      severe: false,
      summary: null,
      detail: null,
      expiresOn: '2026-11-30',
      keptWhere: 'with them',
      since: '2024-05-12',
    },
  ],
  profile: {
    insuranceCarrier: 'Blue Cross Blue Shield of Michigan',
    insuranceGroup: '007123',
    insuranceMember: 'XYZ88214401',
    physicianName: 'Dr. Priya Raman',
    physicianPhone: '(248) 555-0110',
    lastPhysicalOn: '2026-01-20',
    weightLbs: 84,
    otcConsent: 'some',
    otcExceptions: 'No ibuprofen',
    lastReviewedAt: '2026-02-01T10:00:00',
  },
});

/** Every person has their own set; the fixture below is cloned per contact on first read. */
const subscriptionsByContact = new Map<number, Subscriptions>();
function subscriptionsFor(contactId: number): Subscriptions {
  let s = subscriptionsByContact.get(contactId);
  if (!s) {
    const p = findPerson(contactId);
    s = {
      ...subscriptions,
      bulkEmailOptOut: p.privacy.bulkEmailOptOut,
      texting: { ...subscriptions.texting, optedIn: !p.privacy.doNotText },
      publications: subscriptions.publications.map((x) => ({
        ...x,
        // The kids get the kids' publication and nothing else, to show the difference.
        subscribed:
          contactId === ME_ID ? x.subscribed : p.isChild ? x.title === 'Kids' : x.isCampus,
      })),
    };
    subscriptionsByContact.set(contactId, s);
  }
  return s;
}
const subscriptions: Subscriptions = {
  publications: [
    {
      publicationId: 1,
      title: 'Woodside Weekly',
      description: 'What is happening across Woodside this week.',
      sortOrder: 1,
      subscribed: true,
      imageUrl: null,
      isCampus: false,
    },
    {
      publicationId: 2,
      title: 'My Campus',
      description: 'News from your campus pastor and team.',
      sortOrder: 2,
      subscribed: true,
      imageUrl: null,
      isCampus: true,
    },
    {
      publicationId: 3,
      title: 'Kids',
      description: 'Woodside Kids updates for parents.',
      sortOrder: 3,
      subscribed: true,
      imageUrl: null,
      isCampus: false,
    },
    {
      publicationId: 4,
      title: 'Students',
      description: 'Middle and high school ministry.',
      sortOrder: 4,
      subscribed: false,
      imageUrl: null,
      isCampus: false,
    },
    {
      publicationId: 5,
      title: 'Life Groups',
      description: 'Group launches and leader training.',
      sortOrder: 5,
      subscribed: false,
      imageUrl: null,
      isCampus: false,
    },
    {
      publicationId: 6,
      title: 'Men',
      description: 'Men’s events and studies.',
      sortOrder: 6,
      subscribed: true,
      imageUrl: null,
      isCampus: false,
    },
    {
      publicationId: 7,
      title: 'Women',
      description: 'Women’s conference and studies.',
      sortOrder: 7,
      subscribed: false,
      imageUrl: null,
      isCampus: false,
    },
    {
      publicationId: 8,
      title: 'Outreach',
      description: 'Local and global serving trips.',
      sortOrder: 8,
      subscribed: false,
      imageUrl: null,
      isCampus: false,
    },
  ],
  bulkEmailOptOut: false,
  texting: { optedIn: true, typeId: 1 },
};

const giving: Record<number, Giving['donations']> = {
  2026: [
    {
      donationId: 1,
      date: '2026-09-06T09:12:00',
      amount: 250,
      paymentType: 'ACH',
      distributions: [{ program: 'General Fund', amount: 250 }],
    },
    {
      donationId: 2,
      date: '2026-08-02T09:10:00',
      amount: 250,
      paymentType: 'ACH',
      distributions: [{ program: 'General Fund', amount: 250 }],
    },
    {
      donationId: 3,
      date: '2026-07-05T09:11:00',
      amount: 300,
      paymentType: 'ACH',
      distributions: [
        { program: 'General Fund', amount: 250 },
        { program: 'Missions', amount: 50 },
      ],
    },
    {
      donationId: 4,
      date: '2026-06-07T09:09:00',
      amount: 250,
      paymentType: 'ACH',
      distributions: [{ program: 'General Fund', amount: 250 }],
    },
    {
      donationId: 5,
      date: '2026-05-03T09:08:00',
      amount: 250,
      paymentType: 'ACH',
      distributions: [{ program: 'General Fund', amount: 250 }],
    },
    {
      donationId: 6,
      date: '2026-04-05T09:14:00',
      amount: 400,
      paymentType: 'Credit Card',
      distributions: [
        { program: 'General Fund', amount: 250 },
        { program: 'Easter Offering', amount: 150 },
      ],
    },
    {
      donationId: 7,
      date: '2026-03-01T09:07:00',
      amount: 250,
      paymentType: 'ACH',
      distributions: [{ program: 'General Fund', amount: 250 }],
    },
    {
      donationId: 8,
      date: '2026-02-01T09:06:00',
      amount: 250,
      paymentType: 'ACH',
      distributions: [{ program: 'General Fund', amount: 250 }],
    },
    {
      donationId: 9,
      date: '2026-01-04T09:05:00',
      amount: 250,
      paymentType: 'ACH',
      distributions: [{ program: 'General Fund', amount: 250 }],
    },
  ],
  2025: [
    {
      donationId: 21,
      date: '2025-12-21T09:05:00',
      amount: 1000,
      paymentType: 'Check',
      distributions: [{ program: 'Year-End Offering', amount: 1000 }],
    },
    {
      donationId: 22,
      date: '2025-11-02T09:05:00',
      amount: 250,
      paymentType: 'ACH',
      distributions: [{ program: 'General Fund', amount: 250 }],
    },
    {
      donationId: 23,
      date: '2025-10-05T09:05:00',
      amount: 250,
      paymentType: 'ACH',
      distributions: [{ program: 'General Fund', amount: 250 }],
    },
    {
      donationId: 24,
      date: '2025-09-07T09:05:00',
      amount: 250,
      paymentType: 'ACH',
      distributions: [{ program: 'General Fund', amount: 250 }],
    },
  ],
};

const invoices: Invoice[] = [
  {
    invoiceId: 88213,
    date: '2026-08-30T18:41:00',
    total: 390,
    paid: 100,
    balance: 290,
    status: 'Open',
    statusId: 1,
    lines: [
      { item: 'Fall Retreat 2026, Student', recipient: 'Nora Bennett', amount: 195 },
      { item: 'Fall Retreat 2026, Student', recipient: 'Eli Bennett', amount: 195 },
    ],
    payUrl: 'https://woodsidebible.org/pay/?invoiceId=88213',
  },
  {
    invoiceId: 86120,
    date: '2026-06-14T12:03:00',
    total: 45,
    paid: 45,
    balance: 0,
    status: 'Paid',
    statusId: 2,
    lines: [{ item: 'Men’s Breakfast, Adult', recipient: 'Dan Bennett', amount: 45 }],
    payUrl: null,
  },
];

const groups: FinderGroup[] = [
  {
    id: 3101,
    title: 'Bennett Life Group',
    description:
      'Couples and families from Troy meeting on Thursday nights. Kids are welcome; we take turns hosting.',
    full: false,
    paused: false,
    day: 'Thursday',
    occurrence: 'Every Thursday',
    time: '7:00 PM',
    city: 'Troy',
    campusId: 2,
    campusName: 'Troy Campus',
    lifeStageId: 3,
    lifeStage: 'Families',
    kidsWelcome: true,
    householdTypeId: 1,
    householdType: 'Couples',
    genderId: null,
    gender: null,
    leaders: [
      { name: 'Dan Bennett', imageUrl: null },
      { name: 'Rachel Bennett', imageUrl: null },
    ],
    seeJesus: false,
    keywords: [],
  },
  {
    id: 3244,
    title: 'Men at Dawn',
    description: 'Early Tuesday coffee and a chapter of Scripture before work.',
    full: false,
    paused: false,
    day: 'Tuesday',
    occurrence: 'Every Tuesday',
    time: '6:15 AM',
    city: 'Rochester Hills',
    campusId: 2,
    campusName: 'Troy Campus',
    lifeStageId: null,
    lifeStage: null,
    kidsWelcome: false,
    householdTypeId: null,
    householdType: null,
    genderId: 1,
    gender: 'Men',
    leaders: [{ name: 'Tom Okafor', imageUrl: null }],
    seeJesus: false,
    keywords: [],
  },
  {
    id: 3300,
    title: 'Young Adults Troy',
    description: 'Twenties and thirties, Sunday evenings.',
    full: true,
    paused: false,
    day: 'Sunday',
    occurrence: 'Every Sunday',
    time: '6:00 PM',
    city: 'Troy',
    campusId: 2,
    campusName: 'Troy Campus',
    lifeStageId: 2,
    lifeStage: 'Young Adults',
    kidsWelcome: null,
    householdTypeId: null,
    householdType: null,
    genderId: null,
    gender: null,
    leaders: [{ name: 'Casey Lin', imageUrl: null }],
    seeJesus: false,
    keywords: [],
  },
];

const myGroups: MyGroup[] = [
  {
    id: 3101,
    name: 'Bennett Life Group',
    role: 'leader',
    address: { line1: '1420 Maple Grove Ln', line2: null, city: 'Troy', state: 'MI', zip: '48085' },
    members: [
      { contactId: ME_ID, name: 'Dan Bennett', imageUrl: null, role: 'leader' },
      { contactId: 101, name: 'Rachel Bennett', imageUrl: null, role: 'leader' },
    ],
    connectUrl: 'https://my.woodsidebible.org/portal/group_details.aspx?id=3101',
  },
  {
    id: 3244,
    name: 'Men at Dawn',
    role: 'member',
    address: null,
    members: [{ contactId: ME_ID, name: 'Dan Bennett', imageUrl: null, role: 'member' }],
    connectUrl: 'https://my.woodsidebible.org/portal/group_details.aspx?id=3244',
  },
];

const DIRECTORY = [
  { contactId: 301, displayName: 'Linda Bennett', campus: 'Lake Orion Campus' },
  { contactId: 302, displayName: 'Robert Bennett', campus: 'Lake Orion Campus' },
  { contactId: 303, displayName: 'Sofia Alvarez', campus: 'Troy Campus' },
  { contactId: 304, displayName: 'Mark Alvarez', campus: 'Troy Campus' },
  { contactId: 305, displayName: 'Grace Whitfield', campus: 'Romeo Campus' },
  { contactId: 306, displayName: 'Jonah Whitfield', campus: 'Romeo Campus' },
  { contactId: 307, displayName: 'Beth Nakamura', campus: 'Farmington Hills Campus' },
  { contactId: 308, displayName: 'Sam Okafor', campus: 'Warren Campus' },
  { contactId: 309, displayName: 'Tom Okafor', campus: 'Troy Campus' },
];

function allPeople(): Person[] {
  return [me, ...members];
}

function findPerson(contactId: number): Person {
  const p = allPeople().find((x) => x.contactId === contactId);
  if (!p) throw new ApiError('Not in your household', 403);
  return p;
}

function labelFor(relationshipId: number, genderId: number | null): string {
  for (const t of lookups.relationshipTypes) {
    const r = t.relationships.find((x) => x.id === relationshipId);
    if (r) return (genderId === 1 ? r.maleLabel : genderId === 2 ? r.femaleLabel : null) ?? r.name;
  }
  return 'Relative';
}

function typeFor(relationshipId: number) {
  return lookups.relationshipTypes.find((t) =>
    t.relationships.some((r) => r.id === relationshipId)
  );
}

/** `unlinked`: the same family, but the marriage was never written down (no "Married to" row). */
export type MockPersona = 'default' | 'unlinked';

export function createMockApi(
  opts: { signedOut?: boolean; persona?: MockPersona } = {}
): ProfileApi {
  if (opts.persona === 'unlinked') spouse.relationship = null;
  const gate = () => {
    if (opts.signedOut) throw new ApiError('Sign in to see your profile', 401);
  };
  const snapshot = (): ProfileMe => ({
    me: { ...me, displayName: `${me.nickname ?? me.firstName} ${me.lastName}` },
    household: { ...household },
    members: members.map((m) => ({
      ...m,
      displayName: `${m.nickname ?? m.firstName} ${m.lastName}`,
    })),
    lookups,
  });

  return {
    async getMe() {
      await wait(500);
      gate();
      return snapshot();
    },
    async getFormerMembers() {
      await wait(300);
      gate();
      return former.map((f) => ({ ...f }));
    },
    async archiveMember(contactId) {
      await wait();
      gate();
      const i = members.findIndex((m) => m.contactId === contactId);
      if (i < 0) throw new ApiError('Not in your household', 403);
      const [p] = members.splice(i, 1);
      archived.set(contactId, p);
      former.unshift({
        contactId,
        firstName: p.nickname ?? p.firstName,
        name: `${p.nickname ?? p.firstName} ${p.lastName}`,
        imageUrl: p.imageUrl,
        position: 'Guest Child',
        leftOn: new Date().toISOString().slice(0, 19),
        canBringBack: true,
        nowHouseholdName: null,
      });
    },
    async bringBackMember(contactId) {
      await wait();
      gate();
      const p = archived.get(contactId);
      if (!p) throw new ApiError('They live in another household now.', 409);
      archived.delete(contactId);
      members.push(p);
      former = former.filter((f) => f.contactId !== contactId);
    },
    async getLocations() {
      await wait(200);
      // Enough of the locations payload for the map: a pin per campus.
      return MOCK_LOCATIONS;
    },
    async getRequests() {
      await wait(600);
      gate();
      // A head of household answers for everyone in the house.
      const answerFor =
        me.householdPositionId === 1 ? allPeople().map((p) => p.contactId) : [ME_ID];
      return {
        incoming: requests.filter(
          (r) => answerFor.includes(r.toContactId) && r.status === 'pending'
        ),
        outgoing: requests.filter((r) => r.fromContactId === ME_ID && r.status === 'pending'),
      };
    },
    async getMedical(contactId) {
      await wait(400);
      gate();
      findPerson(contactId);
      const m = medical.get(contactId) ?? { items: [], profile: null };
      return {
        items: [...m.items],
        profile: m.profile ? { ...m.profile } : null,
        types: medicalTypes,
      };
    },
    async getSubscriptions(contactId) {
      await wait(700);
      gate();
      const s = subscriptionsFor(contactId);
      return { ...s, publications: s.publications.map((p) => ({ ...p })) };
    },
    async getCommitments() {
      await wait(500);
      gate();
      const pledges = [
        {
          pledgeId: 9001,
          campaignId: 194,
          campaignName: 'Future 2026 Campaign',
          campaignStart: '2025-06-01',
          campaignEnd: null,
          status: 'active',
          statusLabel: 'Active',
          total: 17000,
          given: 4550,
          remaining: 12450,
          installmentsPlanned: 24,
          installmentsPerYear: 12,
          firstInstallmentDate: '2025-12-01',
          beneficiary: null,
          giveUrl: null,
        },
        {
          pledgeId: 9002,
          campaignId: 218,
          campaignName: '07.05.26 Costa Rica | Collective Young Adults',
          campaignStart: '2025-12-09',
          campaignEnd: '2026-08-09',
          status: 'completed',
          statusLabel: 'Completed',
          total: 500,
          given: 500,
          remaining: 0,
          installmentsPlanned: 1,
          installmentsPerYear: 1,
          firstInstallmentDate: '2026-01-15',
          beneficiary: 'Nora Bennett',
          giveUrl: null,
        },
      ] satisfies Commitment[];
      return { pledges, campaign: campaignCommitments.map((c) => ({ ...c })) };
    },
    async updateCampaignCommitment(commitmentId, patch) {
      await wait(600);
      gate();
      const i = campaignCommitments.findIndex((c) => c.commitmentId === commitmentId);
      if (i < 0) throw new ApiError('That commitment is gone', 404);
      const projectedAnnual = patch.previousAnnual + patch.expandedAnnual;
      const projectedTwoYear = projectedAnnual * 2;
      campaignCommitments[i] = {
        ...campaignCommitments[i],
        ...patch,
        projectedAnnual,
        projectedTwoYear,
        total: projectedTwoYear + patch.storedResources,
      };
      return { ...campaignCommitments[i] };
    },
    async removeCampaignCommitment(commitmentId) {
      await wait(600);
      gate();
      const i = campaignCommitments.findIndex((c) => c.commitmentId === commitmentId);
      if (i >= 0) campaignCommitments.splice(i, 1);
    },
    async getGivingStatements() {
      await wait(500);
      gate();
      return [2026, 2025, 2024].map((year) => ({
        statementId: 130000 + year,
        year,
        company: 'Woodside Bible Church',
        fileName: `${year}-Bennett-Statement.pdf`,
        url: `https://example.com/statements/${year}.pdf`,
      }));
    },
    async getGiving(year) {
      await wait(650);
      gate();
      const years = Object.keys(giving)
        .map(Number)
        .sort((a, b) => b - a);
      const y = year && giving[year] ? year : years[0];
      const donations = giving[y] ?? [];
      return { years, year: y, total: donations.reduce((s, d) => s + d.amount, 0), donations };
    },
    async getInvoices() {
      await wait(550);
      gate();
      return invoices.map((i) => ({ ...i }));
    },
    async getGroupCatalogue() {
      await wait(300);
      return { groups, detailsUrl: 'https://woodsidebible.org/group-finder/details/?id=' };
    },
    async getMyGroups() {
      await wait(800);
      gate();
      return myGroups;
    },
    async searchPeople(q) {
      await wait(300);
      gate();
      const needle = q.trim().toLowerCase();
      return DIRECTORY.filter((p) => p.displayName.toLowerCase().includes(needle))
        .slice(0, 8)
        .map((p) => ({ ...p, imageUrl: null }));
    },

    async updateContact(contactId, patch) {
      await wait();
      gate();
      const p = findPerson(contactId);
      Object.assign(p, patch);
      if (patch.dateOfBirth !== undefined) {
        p.age = patch.dateOfBirth
          ? Math.floor((Date.now() - new Date(patch.dateOfBirth).getTime()) / (365.25 * 864e5))
          : null;
      }
      if (patch.householdPositionId !== undefined) {
        p.position =
          lookups.householdPositions.find((o) => o.id === patch.householdPositionId)?.label ?? null;
        p.isChild = patch.householdPositionId === 2 || patch.householdPositionId === 5;
      }
      p.displayName = `${p.nickname ?? p.firstName} ${p.lastName}`;
    },
    async updatePrivacy(contactId, patch) {
      await wait();
      gate();
      const p = findPerson(contactId);
      p.privacy = { ...p.privacy, ...patch };
      if (patch.bulkEmailOptOut !== undefined)
        subscriptionsFor(contactId).bulkEmailOptOut = patch.bulkEmailOptOut;
    },
    async uploadPhoto(contactId, file) {
      await wait(900);
      gate();
      const p = findPerson(contactId);
      const imageUrl = URL.createObjectURL(file);
      p.imageUrl = imageUrl;
      return { imageUrl };
    },
    async removePhoto(contactId) {
      await wait();
      gate();
      findPerson(contactId).imageUrl = null;
    },
    async setEmergencyContact(contactId, input) {
      await wait();
      gate();
      const p = findPerson(contactId);
      let next: EmergencyContact | null;
      if ('relatedContactId' in input) {
        if (input.relatedContactId === null) next = null;
        else {
          const r = findPerson(input.relatedContactId);
          next = {
            kind: 'person',
            contactId: r.contactId,
            name: r.displayName,
            phone: r.mobilePhone,
            imageGuid: null,
            imageUrl: r.imageUrl,
          };
        }
      } else {
        next = {
          kind: 'external',
          contactId: null,
          name: input.name,
          phone: input.phone,
          imageGuid: null,
        };
      }
      p.emergencyContact = next;
      return next;
    },
    async updateAddress(address, kind = 'primary') {
      await wait();
      gate();
      household =
        kind === 'alt'
          ? { ...household, altAddress: { ...address } }
          : { ...household, address: { ...address } };
      return { ...address };
    },
    async setHouseholdCampus(congregationId) {
      await wait();
      gate();
      const c = CAMPUSES.find((x) => x.id === congregationId);
      household = { ...household, congregationId, congregationName: c?.name ?? null };
    },
    async setHomeCampus(congregationId) {
      await wait(100);
      gate();
      me.webCongregationId = congregationId;
    },
    async sendRequest(input) {
      await wait();
      gate();
      if (allPeople().some((p) => p.contactId === input.toContactId))
        throw new ApiError('They are already in your household', 400);
      const to = DIRECTORY.find((p) => p.contactId === input.toContactId);
      const r: RelationshipRequest = {
        id: nextId++,
        fromContactId: ME_ID,
        toContactId: input.toContactId,
        from: { contactId: ME_ID, displayName: me.displayName, imageUrl: me.imageUrl },
        to: {
          contactId: input.toContactId,
          displayName: to?.displayName ?? 'Someone',
          imageUrl: null,
        },
        relationshipId: input.relationshipId,
        label: labelFor(input.relationshipId, null),
        note: input.note ?? null,
        status: 'pending',
        createdAt: nowWall(),
        respondedAt: null,
      };
      requests.push(r);
      return r;
    },
    async answerRequest(id, action) {
      await wait();
      gate();
      const r = requests.find((x) => x.id === id);
      if (!r) throw new ApiError('Request not found', 404);
      r.status = action === 'accept' ? 'accepted' : action === 'decline' ? 'declined' : 'cancelled';
      r.respondedAt = nowWall();
      if (action === 'accept') {
        // Their side: the reciprocal label lands on the asked person's list.
        const rel = lookups.relationshipTypes
          .flatMap((t) => t.relationships)
          .find((x) => x.id === r.relationshipId);
        const mineId = rel?.reciprocalId ?? r.relationshipId;
        const t = typeFor(mineId);
        findPerson(r.toContactId).family.push({
          contactRelationshipId: nextId++,
          relationshipId: mineId,
          label: labelFor(mineId, null),
          editable: true,
          typeId: t?.id ?? null,
          typeName: t?.name ?? null,
          contactId: r.from.contactId,
          displayName: r.from.displayName,
          imageUrl: r.from.imageUrl,
          householdId: null,
        });
      }
    },
    async addRelationship(relatedContactId, relationshipId, subjectContactId = ME_ID) {
      await wait();
      gate();
      const subject = findPerson(subjectContactId);
      const other = findPerson(relatedContactId);
      const id = nextId++;
      const rel = lookups.relationshipTypes
        .flatMap((t) => t.relationships)
        .find((r) => r.id === relationshipId);
      const mirrorId = rel?.reciprocalId ?? relationshipId;
      subject.relations = [
        ...subject.relations.filter((r) => r.contactId !== relatedContactId),
        relation(relatedContactId, relationshipId, id),
      ];
      other.relations = [
        ...other.relations.filter((r) => r.contactId !== subjectContactId),
        relation(subjectContactId, mirrorId, nextId++),
      ];
      for (const m of members) {
        m.relationship =
          me.relations.find((r) => r.contactId === m.contactId)?.relationship ?? null;
      }
      return subject.relations.find((r) => r.contactId === relatedContactId)!.relationship;
    },
    async changeRelationship(contactRelationshipId, relationshipId) {
      await wait();
      gate();
      const t = typeFor(relationshipId);
      const next = {
        contactRelationshipId: contactRelationshipId + 100000,
        relationshipId,
        label: labelFor(relationshipId, null),
        typeId: t?.id ?? null,
        typeName: t?.name ?? null,
      };
      for (const p of [me, ...members]) {
        const i = p.family.findIndex((f) => f.contactRelationshipId === contactRelationshipId);
        if (i >= 0) p.family[i] = { ...p.family[i], ...next };
      }
      return next;
    },
    async removeRelationship(contactRelationshipId) {
      await wait();
      gate();
      for (const p of [me, ...members]) {
        const hit = p.relations.find(
          (r) => r.relationship.contactRelationshipId === contactRelationshipId
        );
        if (!hit) continue;
        // End the pair: this row and its mirror on the other person.
        const otherId = hit.contactId;
        p.relations = p.relations.filter((r) => r.contactId !== otherId);
        const other = findPerson(otherId);
        other.relations = other.relations.filter((r) => r.contactId !== p.contactId);
      }
      for (const m of members) {
        m.relationship =
          me.relations.find((r) => r.contactId === m.contactId)?.relationship ?? null;
      }
      const i = family.findIndex((f) => f.contactRelationshipId === contactRelationshipId);
      if (i >= 0) family.splice(i, 1);
    },
    async addMedicalItem(contactId, input) {
      await wait();
      gate();
      findPerson(contactId);
      const m = medical.get(contactId) ?? { items: [], profile: null };
      medical.set(contactId, m);
      const item: MedicalItem = {
        id: nextId++,
        kind: input.kind,
        typeId: input.typeId ?? null,
        label: input.label,
        severe: input.severe ?? false,
        summary: input.summary ?? null,
        detail: input.detail ?? null,
        expiresOn: input.expiresOn ?? null,
        keptWhere: input.keptWhere ?? null,
        since: nowWall().slice(0, 10),
      };
      m.items.push(item);
      return { ...item };
    },
    async updateMedicalItem(contactId, itemId, patch) {
      await wait();
      gate();
      const item = medical.get(contactId)?.items.find((i) => i.id === itemId);
      if (item) Object.assign(item, patch);
    },
    async removeMedicalItem(contactId, itemId) {
      await wait();
      gate();
      const m = medical.get(contactId);
      if (m) m.items = m.items.filter((i) => i.id !== itemId);
    },
    async updateMedicalProfile(contactId, patch) {
      await wait();
      gate();
      const m = medical.get(contactId) ?? { items: [], profile: null };
      medical.set(contactId, m);
      m.profile = {
        insuranceCarrier: null,
        insuranceGroup: null,
        insuranceMember: null,
        physicianName: null,
        physicianPhone: null,
        lastPhysicalOn: null,
        weightLbs: null,
        otcConsent: null,
        otcExceptions: null,
        lastReviewedAt: null,
        ...(m.profile ?? {}),
        ...patch,
      };
      return { ...m.profile };
    },
    async confirmMedical(contactId) {
      await wait();
      gate();
      const m = medical.get(contactId) ?? { items: [], profile: null };
      medical.set(contactId, m);
      m.profile = {
        insuranceCarrier: null,
        insuranceGroup: null,
        insuranceMember: null,
        physicianName: null,
        physicianPhone: null,
        lastPhysicalOn: null,
        weightLbs: null,
        otcConsent: null,
        otcExceptions: null,
        ...(m.profile ?? {}),
        lastReviewedAt: nowWall(),
      };
      return { ...m.profile };
    },
    async updateSubscriptions(contactId, patch) {
      await wait();
      gate();
      const person = findPerson(contactId);
      let s = subscriptionsFor(contactId);
      if (patch.publications) {
        for (const p of patch.publications) {
          const pub = s.publications.find((x) => x.publicationId === p.publicationId);
          if (pub) pub.subscribed = p.subscribed;
        }
      }
      if (patch.bulkEmailOptOut !== undefined) {
        s.bulkEmailOptOut = patch.bulkEmailOptOut;
        person.privacy.bulkEmailOptOut = patch.bulkEmailOptOut;
      }
      if (patch.textingOptedIn !== undefined) {
        s = { ...s, texting: { ...s.texting, optedIn: patch.textingOptedIn } };
        subscriptionsByContact.set(contactId, s);
        person.privacy.doNotText = !patch.textingOptedIn;
      }
      return { ...s, publications: s.publications.map((p) => ({ ...p })) };
    },
    async changePassword(currentPassword) {
      await wait(700);
      gate();
      if (currentPassword !== 'password')
        throw new ApiError('Your current password is not right', 400);
    },
  };
}
